import { useCreateEmployee, useUpdateUser } from "@/hooks/useUser";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import {
  FormControl,
  FormItem,
  FormMessage,
  FormField,
  FormLabel,
  Form,
} from "../ui/form";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanies } from "@/hooks/useCompanies";
import { Check, Loader2 } from "lucide-react";

import { Button } from "../ui/button";
import type { UserType } from "@/types/user.type";
import { Switch } from "../ui/switch";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { showErrorToast, showSuccessToast } from "../ui/toast";

const employeeSchema = z.object({
  employeeNumber: z.string().optional(),
  firstName: z.string().min(3, "Le prénom doit contenir au moins 3 caractères"),
  lastName: z
    .string()
    .min(2, "Le nom de famille doit contenir au moins 2 caractères"),
  phoneNumber: z.string().optional(),
  address: z.string().optional(),
  postalCode: z.string().optional(),
  city: z.string().optional(),
  pin: z.string().min(6, "Le PIN doit contenir au moins 6 caractères"),
  companyId: z.string().min(1, "L'entreprise est requise"),
  isActive: z.boolean(),
});

type EmployeeFormValues = z.infer<typeof employeeSchema>;

interface EmployeeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  employee?: UserType | null;
}

export const EmployeeFormDialog = ({
  open,
  onOpenChange,
  onSuccess,
  employee,
}: EmployeeFormDialogProps) => {
  const { user: authUser } = useAuth();
  const isSuperuser = authUser?.role === "SUPERUSER";
  const isEditing = !!employee;
  const [companySearch, setCompanySearch] = useState("");
  const [companyPopoverOpen, setCompanyPopoverOpen] = useState(false);

  const defaultValues = useMemo<EmployeeFormValues>(
    () => ({
      employeeNumber: "",
      firstName: "",
      lastName: "",
      phoneNumber: "",
      address: "",
      postalCode: "",
      city: "",
      pin: "",
      companyId: isSuperuser ? "" : authUser?.companyId ?? "",
      isActive: true,
    }),
    [authUser?.companyId, isSuperuser]
  );

  const { mutate: createEmployeeMutation, isPending: isCreatingEmployee } =
    useCreateEmployee();
  const { mutate: updateEmployeeMutation, isPending: isUpdatingEmployee } =
    useUpdateUser();

  const form = useForm<EmployeeFormValues>({
    resolver: zodResolver(employeeSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty },
  } = form;

  useEffect(() => {
    if (isEditing) {
      reset({
        employeeNumber: employee.employeeNumber,
        firstName: employee.firstName,
        lastName: employee.lastName,
        phoneNumber: employee.phoneNumber || "", // si l'employé n'a pas de numéro de téléphone, on lui donne un numéro de téléphone par défaut
        address: employee.address || "", // si l'employé n'a pas d'adresse, on lui donne une adresse par défaut
        postalCode: employee.postalCode || "", // si l'employé n'a pas de code postal, on lui donne un code postal par défaut
        city: employee.city || "",
        pin: employee.pin,
        companyId: employee.companyId,
        isActive: employee.isActive,
      });
    } else {
      reset(defaultValues);
    }
  }, [isEditing, reset, defaultValues, employee]);

  const { data: companiesData, isFetching: isFetchingCompanies } = useCompanies(
    isSuperuser
      ? { page: 1, limit: 100, search: companySearch, isActive: true }
      : undefined,
    {
      enabled: isSuperuser && open, // la requête est exécutée si isSuperuser est true, sinon la requête n'est pas exécutée et open est true
    }
  );

  const companyOptions = companiesData?.companies ?? [];



  //2eme méthode pour récupérer le nom de l'entreprise courante
  let currentCompanyName = "Sélectionne une entreprise";

  //
  const currentCompany = companyOptions.find(
    (company) => company.id === form.getValues("companyId")
  ); // renvoie l'entreprise courante ou undefined donc on peut vérifier si l'entreprise courante existe et si elle a un nom
  // si l'entreprise courante existe et si elle a un nom, on l'assigne à currentCompanyName
  if (currentCompany && currentCompany.name) {
    currentCompanyName = currentCompany.name;
  } else if (employee?.company?.name) {
    // si l'entreprise courante n'existe pas ou n'a pas de nom, on récupère le nom de l'entreprise de l'employé
    currentCompanyName = employee.company.name;
  }

  const onSubmit = (data: EmployeeFormValues) => {
    if (isEditing && employee?.id) {
      updateEmployeeMutation(
        { id: employee.id, data: data },
        {
          onSuccess: () => {
            showSuccessToast("Employé modifié avec succès");
            reset(defaultValues);
            onOpenChange(false);
            onSuccess?.(); // appelle la fonction onSuccess passée en prop
          },
          onError: (error: Error) => {
            showErrorToast(
              error.message ||
                "Une erreur est survenue lors de la modification de l'employé"
            );
          },
        }
      );
      return;
    }

    const payload = {
      ...data,
      role: "EMPLOYEE" as const,
    };

    createEmployeeMutation(payload, {
      onSuccess: async () => {
        showSuccessToast("Employé créé avec succès");
        reset(defaultValues);
        onOpenChange(false);
        onSuccess?.(); // on succes en props
      },
      onError: (error: Error) => {
        showErrorToast(
          error.message ||
            "Une erreur est survenue lors de la création de l'employé"
        );
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[calc(100%-1rem)] sm:max-w-[600px] md:max-w-[800px] p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl">
            {isEditing ? "Modifier l'employé" : "Nouvel employé"}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {isEditing
              ? "Modifiez les informations de l'employé."
              : "Ajoutez un nouvel employé au système."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-4 border p-2 rounded-lg sm:p-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <FormField
                control={control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm sm:text-base font-semibold">
                      Prénom *
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm sm:text-base font-semibold">
                      Nom de famille *
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="employeeNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm sm:text-base">
                      Matricule
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Généré automatiquement si vide"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm sm:text-base">
                      Téléphone
                    </FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="+33123456789" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="address"
                render={({ field }) => (
                  <FormItem className="col-span-1 sm:col-span-2">
                    <FormLabel className="text-sm sm:text-base">
                      Adresse
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="postalCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm sm:text-base">
                      Code postal
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm sm:text-base">
                      Ville
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="pin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm sm:text-base font-semibold">
                      PIN *
                    </FormLabel>
                    <FormControl>
                      <Input type="text" placeholder="123456" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {isEditing && (
                <FormField
                  control={control}
                  name="isActive"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm sm:text-base font-semibold">
                        Statut
                      </FormLabel>
                      <FormControl>
                        <div className="flex items-center gap-3 rounded-lg border p-2">
                          <span className="text-sm text-muted-foreground">
                            {field.value ? "Actif" : "Inactif"}
                          </span>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {isSuperuser && (
                <FormField
                  control={control}
                  name="companyId"
                  render={({ field }) => (
                    <FormItem
                      className={isEditing ? "col-span-1 sm:col-span-1" : ""}
                    >
                      <FormLabel className="text-sm sm:text-base font-semibold">
                        Entreprise *
                      </FormLabel>
                      <Popover
                        open={companyPopoverOpen}
                        onOpenChange={(nextOpen) => {
                          setCompanyPopoverOpen(nextOpen);
                          if (!nextOpen) {
                            setCompanySearch("");
                          }
                        }}
                      >
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              role="combobox"
                              className="w-full justify-between"
                            >
                              {currentCompanyName}
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-[var(--radix-popover-trigger-width)] p-0"
                          align="start"
                        >
                          <Command shouldFilter={false}>
                            <CommandInput
                              placeholder="Rechercher une entreprise..."
                              value={companySearch}
                              onValueChange={(value) => {
                                setCompanySearch(value);
                              }}
                            />
                            <CommandList>
                              {isFetchingCompanies ? (
                                <div className="py-3 text-center text-sm text-muted-foreground">
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                </div>
                              ) : companyOptions.length === 0 ? (
                                <CommandEmpty>
                                  Aucune entreprise trouvée.
                                </CommandEmpty>
                              ) : (
                                companyOptions.map((company) => (
                                  <CommandItem
                                    key={company.id}
                                    value={company.name}
                                    onSelect={() => {
                                      field.onChange(company.id);
                                      setCompanyPopoverOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        company.id === field.value
                                          ? "opacity-100"
                                          : "opacity-0"
                                      )}
                                    />
                                    {company.name}
                                  </CommandItem>
                                ))
                              )}
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
            <DialogFooter className="flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-2">
              <Button
                type="submit"
                disabled={
                  isEditing
                    ? !isDirty || isUpdatingEmployee
                    : isCreatingEmployee
                }
                className="w-full sm:w-auto order-1 sm:order-2"
              >
                {isCreatingEmployee || isUpdatingEmployee ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    En cours...
                  </>
                ) : isEditing ? (
                  "Modifier"
                ) : (
                  "Créer"
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="w-full sm:w-auto order-2 sm:order-1"
              >
                Annuler
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
